import type { ReactNode } from 'react';
import type { OfferingV2Response } from '../../types/offeringsV2';

/**
 * Spec 081a — service-page texts on the admin workspace Overview tab: headline, Arabic
 * description, what's included (lists), materials & details (table), warranty length and Arabic
 * warranty text, plus an "Options" block with each option's description / more details and each
 * value's description. A field the garage has not filled reads "Not set".
 */
export interface OverviewServiceTextsProps {
  offering: OfferingV2Response;
}

const NOT_SET = <span className="workspace-overview-value--muted">Not set</span>;

const has = (s: string | null | undefined): s is string => !!s && !!s.trim();

function warrantyLabel(days: number): string {
  if (days % 365 === 0) return `${days} days (${days / 365} year${days === 365 ? '' : 's'})`;
  if (days % 30 === 0) return `${days} days (${days / 30} month${days === 30 ? '' : 's'})`;
  return `${days} day${days === 1 ? '' : 's'}`;
}

export default function OverviewServiceTexts({ offering }: OverviewServiceTextsProps) {
  const included = (offering.whatsIncluded ?? []).filter(has);
  const includedAr = (offering.whatsIncludedAr ?? []).filter(has);
  const materials = offering.materialsDetails ?? [];

  const row = (label: string, value: ReactNode, testId: string) => (
    <div className="workspace-overview-row" data-testid={testId}>
      <span className="workspace-overview-label">{label}</span>
      <span className="workspace-overview-value">{value}</span>
    </div>
  );
  const text = (s: string | null | undefined, ar = false) =>
    has(s) ? <span className="workspace-overview-text" dir={ar ? 'rtl' : undefined} lang={ar ? 'ar' : undefined}>{s}</span> : NOT_SET;
  const list = (items: string[], ar = false) => (items.length === 0 ? NOT_SET : (
    <ul className="workspace-overview-list" dir={ar ? 'rtl' : undefined} lang={ar ? 'ar' : undefined}>
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  ));

  const options = [...(offering.variations ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const optionHasText = options.some((v) => has(v.description) || has(v.descriptionAr) || has(v.moreDetails) || has(v.moreDetailsAr)
    || v.values.some((vv) => has(vv.description) || has(vv.descriptionAr)));

  return (
    <>
      {row('Headline (EN)', text(offering.headline), 'overview-headline')}
      {row('Headline (AR)', text(offering.headlineAr, true), 'overview-headline-ar')}
      {row('Description (AR)', text(offering.descriptionAr, true), 'overview-description-ar')}
      {row("What's included (EN)", list(included), 'overview-whats-included')}
      {row("What's included (AR)", list(includedAr, true), 'overview-whats-included-ar')}
      {row('Materials & details', materials.length === 0 ? NOT_SET : (
        <table className="workspace-overview-table">
          <thead>
            <tr><th scope="col">Label</th><th scope="col">Value</th><th scope="col">Label (AR)</th><th scope="col">Value (AR)</th></tr>
          </thead>
          <tbody>
            {materials.map((m, i) => (
              <tr key={i}>
                <td>{m.label || '—'}</td>
                <td>{m.value || '—'}</td>
                <td dir="rtl" lang="ar">{m.labelAr || '—'}</td>
                <td dir="rtl" lang="ar">{m.valueAr || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ), 'overview-materials')}
      {row('Warranty length', offering.warrantyDays != null ? warrantyLabel(offering.warrantyDays) : NOT_SET, 'overview-warranty-days')}
      {row('Warranty (AR)', text(offering.warrantyDescriptionAr, true), 'overview-warranty-ar')}
      {options.length > 0 && row('Options', !optionHasText ? NOT_SET : (
        <div className="workspace-overview-options">
          {options.map((v) => (
            <div key={v.id} className="workspace-overview-option">
              <div className="workspace-overview-option-name">{v.name}</div>
              <dl className="workspace-overview-option-texts">
                <dt>Description</dt><dd>{text(v.description)}</dd>
                <dt>Description (AR)</dt><dd>{text(v.descriptionAr, true)}</dd>
                <dt>More details</dt><dd>{text(v.moreDetails)}</dd>
                <dt>More details (AR)</dt><dd>{text(v.moreDetailsAr, true)}</dd>
              </dl>
              {v.values.some((vv) => has(vv.description) || has(vv.descriptionAr)) && (
                <ul className="workspace-overview-list">
                  {[...v.values].sort((a, b) => a.sortOrder - b.sortOrder)
                    .filter((vv) => has(vv.description) || has(vv.descriptionAr))
                    .map((vv) => (
                      <li key={vv.id}>
                        <strong>{vv.label}</strong>
                        {has(vv.description) && <> — {vv.description}</>}
                        {has(vv.descriptionAr) && <> · <span dir="rtl" lang="ar">{vv.descriptionAr}</span></>}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      ), 'overview-options')}
    </>
  );
}
